import { useState } from 'react';
import ReactModal from 'react-modal';
import General from '../utils/GeneralUtils';
import { getEnumFieldInfo } from '../utils/enum-field-values';
import { CopyToClipboard } from './CopyToClipboard';
import './EnumValuesSlideout.scss';

interface EnumValuesSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  /** Edulead field name, e.g. 'subtype'. Fields without a value set render nothing. */
  fieldKey: string | null;
  /** Named in the picklist hint, e.g. 'Salesforce'. */
  crmName?: string;
}

/**
 * Shows the complete value set a field exports, so a customer can build a
 * matching picklist in their CRM. Values are copyable as a newline-separated
 * list, which is the format Salesforce's picklist editor pastes into.
 */
export function EnumValuesSlideout({ isOpen, onClose, fieldKey, crmName = 'your CRM' }: EnumValuesSlideoutProps) {
  const info = fieldKey ? getEnumFieldInfo(fieldKey) : undefined;

  if (!info) {
    return null;
  }

  const valueList = info.values.join('\n');

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      style={General.getSliderDefaults({ width: 'min(90vw, 550px)' })}
      parentSelector={General.getModalRoot}
    >
      <div className="enum-values-slideout">
        <div className="header">
          <h3>{info.title} values</h3>
          <div className="header-actions">
            <span className="desc-12">{info.values.length} values</span>
            <CopyToClipboard value={valueList} title="Copy all values" />
          </div>
        </div>

        <div className="section intro">
          <p>{info.description}</p>
          <p>
            To store this field in {crmName} as a picklist, configure it with the values below exactly as
            they appear. A restricted picklist rejects any value outside its value set, so a missing value
            fails the record on export. Mapping the field to a text field instead always works.
          </p>
        </div>

        {info.note && <div className="note">{info.note}</div>}

        <div className="section">
          <div className="values-list">
            {info.values.map(value => (
              <span key={value} className="value-chip">{value}</span>
            ))}
          </div>
        </div>
      </div>
    </ReactModal>
  );
}

/**
 * Open/close state for the drawer, so a screen only tracks which field is showing.
 */
export function useEnumValuesSlideout() {
  const [fieldKey, setFieldKey] = useState<string | null>(null);

  return {
    fieldKey,
    isOpen: fieldKey !== null,
    open: setFieldKey,
    close: () => setFieldKey(null),
  };
}
